import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_NAME = "Cargo Power Logistics";
export const BASE_URL = "https://www.cargopowerlogistics.com";
export const TWITTER_HANDLE = "@cargopowerltd";
const DEFAULT_IMAGE = `${BASE_URL}/logo.png`;

/**
 * Dependency-free per-route SEO. Updates <title>, meta description, keywords,
 * robots, canonical link, Open Graph / Twitter tags, and any per-page
 * structured data (JSON-LD) on navigation so every route exposes unique,
 * crawlable metadata. Googlebot renders these client-side updates.
 */
const setMeta = (selector, attr, key, content) => {
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

// Managed JSON-LD: remove any we previously injected, then add the current set.
const setJsonLd = (blocks) => {
  document
    .querySelectorAll('script[data-seo-jsonld="true"]')
    .forEach((s) => s.remove());
  if (!blocks) return;
  const list = Array.isArray(blocks) ? blocks : [blocks];
  list.filter(Boolean).forEach((block) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seoJsonld = "true";
    script.text = JSON.stringify(block);
    document.head.appendChild(script);
  });
};

const Seo = ({
  title,
  description,
  image,
  keywords,
  type = "website",
  noindex = false,
  jsonLd,
}) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const url = `${BASE_URL}${pathname === "/" ? "/" : pathname}`;
    const img = image
      ? image.startsWith("http")
        ? image
        : `${BASE_URL}${image}`
      : DEFAULT_IMAGE;

    document.title = fullTitle;
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[name="keywords"]', "name", "keywords", keywords);
    setMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
    );
    setCanonical(url);

    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:image"]', "property", "og:image", img);
    setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", fullTitle);

    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:site"]', "name", "twitter:site", TWITTER_HANDLE);
    setMeta('meta[name="twitter:creator"]', "name", "twitter:creator", TWITTER_HANDLE);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", img);

    setJsonLd(jsonLd);

    return () => setJsonLd(null);
  }, [title, description, image, keywords, type, noindex, jsonLd, pathname]);

  return null;
};

export default Seo;
